import { useEffect, useState, type FormEvent, type WheelEvent } from 'react';
import { createMantenimiento, updateMantenimiento } from '../../lib';
import { getConductores, getVehiculos, uploadFileToMinio } from '../../lib/storage';
import type { Conductor, Mantenimiento, UpsertMantenimientoInput, Vehiculo } from '../../lib/appTypes';

interface Props {
  initialMantenimiento?: Mantenimiento | null;
  onSaved: (mantenimiento: Mantenimiento) => void;
  onCancel: () => void;
}

function preventNumberInputWheelChange(event: WheelEvent<HTMLInputElement>): void {
  event.currentTarget.blur();
}

const MAX_IMAGENES = 15;

function todayInputValue(): string {
  return new Date().toISOString().slice(0, 10);
}

export default function FormMantenimiento({ initialMantenimiento, onSaved, onCancel }: Props) {
  const [form, setForm] = useState({
    vehiculo: initialMantenimiento?.vehiculo ?? '',
    fechaMantenimiento: initialMantenimiento?.fechaMantenimiento ?? todayInputValue(),
    fechaSalidaMantenimiento: initialMantenimiento?.fechaSalidaMantenimiento ?? '',
    kilometrosMantenimiento: initialMantenimiento?.kilometrosMantenimiento ?? '',
    descripcion: initialMantenimiento?.descripcion ?? '',
    conductorResponsable: initialMantenimiento?.conductorResponsable ?? '',
    responsableNombre: initialMantenimiento?.responsableNombre ?? '',
  });

  const [imagenes, setImagenes] = useState<string[]>(initialMantenimiento?.urlMantenimiento ?? []);
  const [subiendoImagenes, setSubiendoImagenes] = useState(0);
  const [vehiculos, setVehiculos] = useState<Vehiculo[]>([]);
  const [conductores, setConductores] = useState<Conductor[]>([]);
  const [loadingData, setLoadingData] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isEditing = Boolean(initialMantenimiento?.id);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      try {
        setLoadingData(true);
        const [nextVehiculos, nextConductores] = await Promise.all([
          getVehiculos(),
          getConductores(),
        ]);

        if (!cancelled) {
          setVehiculos(nextVehiculos);
          setConductores(nextConductores);
        }
      } catch {
        if (!cancelled) {
          setError('No se pudieron cargar vehículos o conductores.');
        }
      } finally {
        if (!cancelled) {
          setLoadingData(false);
        }
      }
    }

    void run();

    return () => {
      cancelled = true;
    };
  }, []);

  function handle(event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) {
    const { name, value } = event.target;
    setForm(prev => ({ ...prev, [name]: value }));
  }

  function handleConductorChange(event: React.ChangeEvent<HTMLSelectElement>) {
    const conductorResponsable = event.target.value;
    const conductor = conductores.find(item => item.id === conductorResponsable);

    setForm(prev => ({
      ...prev,
      conductorResponsable,
      responsableNombre: conductor ? conductor.nombre : prev.responsableNombre,
    }));
  }

  async function handleImagenes(event: React.ChangeEvent<HTMLInputElement>) {
    const archivos = Array.from(event.target.files ?? []);
    event.target.value = '';
    if (archivos.length === 0) return;

    const noImagenes = archivos.filter(archivo => !archivo.type.startsWith('image/'));
    if (noImagenes.length > 0) {
      setError('Solo se pueden adjuntar imágenes (JPG, PNG, WEBP...).');
      return;
    }
    if (imagenes.length + archivos.length > MAX_IMAGENES) {
      setError(`Se permiten como máximo ${MAX_IMAGENES} imágenes por mantenimiento.`);
      return;
    }

    setError('');
    setSubiendoImagenes(actual => actual + archivos.length);
    try {
      const urls = await Promise.all(archivos.map(archivo => uploadFileToMinio(archivo, 'mantenimientos')));
      setImagenes(prev => [...prev, ...urls]);
    } catch (err: unknown) {
      setError((err as { message?: string })?.message ?? 'No se pudieron subir las imágenes.');
    } finally {
      setSubiendoImagenes(actual => Math.max(0, actual - archivos.length));
    }
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError('');

    if (!form.vehiculo) {
      setError('Selecciona un vehículo.');
      return;
    }

    if (!form.fechaMantenimiento) {
      setError('Selecciona la fecha del mantenimiento.');
      return;
    }

    if (form.fechaSalidaMantenimiento && form.fechaSalidaMantenimiento < form.fechaMantenimiento) {
      setError('La fecha de salida no puede ser anterior a la fecha del mantenimiento.');
      return;
    }

    const kilometros = Number(form.kilometrosMantenimiento);
    if (!Number.isFinite(kilometros) || kilometros < 0) {
      setError('Ingresa kilómetros válidos.');
      return;
    }

    if (!form.descripcion.trim()) {
      setError('Ingresa la descripción del mantenimiento.');
      return;
    }

    if (subiendoImagenes > 0) {
      setError('Espera a que terminen de subirse las imágenes.');
      return;
    }

    if (!form.conductorResponsable && !form.responsableNombre.trim()) {
      setError('Selecciona un conductor responsable o ingresa el nombre del responsable.');
      return;
    }

    const payload: UpsertMantenimientoInput = {
      vehiculo: form.vehiculo,
      fechaMantenimiento: form.fechaMantenimiento,
      fechaSalidaMantenimiento: form.fechaSalidaMantenimiento || null,
      kilometrosMantenimiento: kilometros,
      descripcion: form.descripcion.trim(),
      conductorResponsable: form.conductorResponsable || null,
      responsableNombre: form.responsableNombre.trim() || null,
      urlMantenimiento: imagenes,
    };

    try {
      setLoading(true);
      const mantenimiento = initialMantenimiento
        ? await updateMantenimiento(initialMantenimiento.id, payload)
        : await createMantenimiento(payload);

      onSaved(mantenimiento);
    } catch (err: unknown) {
      setError((err as { message?: string })?.message ?? 'No se pudo guardar el mantenimiento.');
    } finally {
      setLoading(false);
    }
  }

  const inputCls =
    'w-full rounded-2xl border border-stone-300 bg-white px-3 py-2.5 text-sm text-stone-700 outline-none transition focus:border-teal-500 focus:ring-4 focus:ring-teal-100';
  const labelCls = 'mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500';

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div>
        <h3 className="text-lg font-semibold text-stone-900">
          {isEditing ? 'Editar mantenimiento' : 'Registrar mantenimiento'}
        </h3>
        <p className="text-sm text-stone-500">
          Registra el mantenimiento realizado al vehículo, fecha de salida, kilometraje y responsable.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className={labelCls}>Vehículo</label>
          <select
            name="vehiculo"
            value={form.vehiculo}
            onChange={handle}
            required
            disabled={loadingData}
            className={inputCls}
          >
            <option value="">
              {loadingData ? 'Cargando vehículos...' : 'Selecciona un vehículo'}
            </option>
            {vehiculos.map(vehiculo => (
              <option key={vehiculo.id} value={vehiculo.id}>
                {vehiculo.placa} · {vehiculo.marca} {vehiculo.modelo}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelCls}>Fecha de mantenimiento</label>
          <input
            name="fechaMantenimiento"
            type="date"
            value={form.fechaMantenimiento}
            onChange={handle}
            required
            className={inputCls}
          />
        </div>

        <div>
          <label className={labelCls}>Fecha de salida</label>
          <input
            name="fechaSalidaMantenimiento"
            type="date"
            value={form.fechaSalidaMantenimiento}
            onChange={handle}
            className={inputCls}
          />
        </div>

        <div>
          <label className={labelCls}>Kilómetros de mantenimiento</label>
          <input
            name="kilometrosMantenimiento"
            type="number"
            min="0"
            step="1"
            value={form.kilometrosMantenimiento}
            onWheel={preventNumberInputWheelChange}
            onChange={handle}
            required
            className={inputCls}
            placeholder="150000"
          />
        </div>

        <div>
          <label className={labelCls}>Conductor responsable</label>
          <select
            name="conductorResponsable"
            value={form.conductorResponsable}
            onChange={handleConductorChange}
            disabled={loadingData}
            className={inputCls}
          >
            <option value="">Responsable manual / externo</option>
            {conductores.map(conductor => (
              <option key={conductor.id} value={conductor.id}>
                {conductor.nombre}
              </option>
            ))}
          </select>
        </div>

        <div className="md:col-span-2">
          <label className={labelCls}>Nombre responsable</label>
          <input
            name="responsableNombre"
            value={form.responsableNombre}
            onChange={handle}
            className={inputCls}
            placeholder="Nombre del conductor, taller o responsable externo"
          />
        </div>

        <div className="md:col-span-2">
          <label className={labelCls}>Imágenes del mantenimiento</label>
          <div className="rounded-2xl border border-dashed border-stone-300 bg-stone-50/70 p-3">
            <div className="flex flex-wrap items-center gap-3">
              {imagenes.map((url, indice) => (
                <div key={url} className="group relative h-20 w-20 overflow-hidden rounded-xl border border-stone-200 bg-white">
                  <a href={url} target="_blank" rel="noreferrer" title="Ver imagen completa">
                    <img src={url} alt={`Imagen ${indice + 1} del mantenimiento`} className="h-full w-full object-cover" />
                  </a>
                  <button
                    type="button"
                    onClick={() => setImagenes(prev => prev.filter(item => item !== url))}
                    aria-label={`Quitar imagen ${indice + 1}`}
                    title="Quitar imagen"
                    className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-xs text-white opacity-90 transition hover:bg-rose-600"
                  >
                    <i className="fa-solid fa-xmark" aria-hidden="true" />
                  </button>
                </div>
              ))}
              {subiendoImagenes > 0 && (
                <div className="flex h-20 w-20 items-center justify-center rounded-xl border border-stone-200 bg-white text-stone-400" role="status">
                  <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
                  <span className="sr-only">Subiendo imágenes...</span>
                </div>
              )}
              {imagenes.length < MAX_IMAGENES && (
                <label className="flex h-20 w-20 cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border border-stone-300 bg-white text-xs font-semibold text-stone-500 transition hover:border-stone-400 hover:text-stone-700">
                  <i className="fa-solid fa-camera text-base" aria-hidden="true" />
                  Agregar
                  <input type="file" accept="image/*" multiple onChange={event => void handleImagenes(event)} className="sr-only" />
                </label>
              )}
            </div>
            <p className="mt-2 text-xs text-stone-500">
              Opcional. Puedes adjuntar una o varias imágenes (máximo {MAX_IMAGENES}).
              {imagenes.length > 0 && ` ${imagenes.length} adjunta${imagenes.length === 1 ? '' : 's'}.`}
            </p>
          </div>
        </div>

        <div className="md:col-span-2">
          <label className={labelCls}>Descripción</label>
          <textarea
            name="descripcion"
            value={form.descripcion}
            onChange={handle}
            required
            rows={5}
            className={inputCls}
            placeholder="Cambio de aceite, revisión de frenos, cambio de filtros..."
          />
        </div>
      </div>

      {error && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="ui-btn ui-btn-secondary"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={loading || loadingData || subiendoImagenes > 0 || vehiculos.length === 0}
          className="ui-btn ui-btn-primary"
        >
          {loading ? 'Guardando...' : isEditing ? 'Guardar cambios' : 'Registrar mantenimiento'}
        </button>
      </div>
    </form>
  );
}
